import { ChevronRight, Wallet } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableCellLink,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { DashboardTarget } from "@/lib/api/dashboard";
import { useDashboardWidget } from "@/lib/hooks/use-dashboard";
import { useT } from "@/lib/i18n/use-t";
import { useDashboardFormat } from "../use-dashboard-format";
import { T } from "./cash-to-invest-widget.i18n";
import { InitialsAvatar } from "./initials-avatar";
import { WidgetCard } from "./widget-card";
import { WidgetEmpty } from "./widget-empty";

/**
 * The share bar reads 0–25 %: cash rarely passes a quarter of a portfolio, and
 * a bar scaled to 100 % would leave every row a sliver.
 */
const SHARE_BAR_FULL = 0.25;

export function CashToInvestWidget({ target }: { target: DashboardTarget }) {
  const { data, isLoading, error } = useDashboardWidget("cash_to_invest", target);
  const t = useT(T);
  const { count, amount, percent } = useDashboardFormat();

  return (
    <WidgetCard
      icon={Wallet}
      title={t.cashToInvest}
      description={
        data ? t.cashToInvestDescription(`${data.currency} ${amount(data.threshold)}`) : undefined
      }
      badge={data ? t.clientCount(count(data.clientCount)) : undefined}
      isLoading={isLoading}
      error={error}
    >
      {data &&
        (data.clients.length === 0 ? (
          <WidgetEmpty>{t.noCash}</WidgetEmpty>
        ) : (
          <Table variant="navigation" surface={false}>
            <TableHeader>
              <TableRow>
                <TableHead>{t.colClient}</TableHead>
                <TableHead className="text-right">{t.colCash(data.currency)}</TableHead>
                <TableHead className="pl-12">{t.colShare}</TableHead>
                <TableHead className="w-10" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.clients.map((client) => (
                <TableRow key={client.clientId}>
                  <TableCellLink
                    to={`/clients/${client.clientId}`}
                    linkClassName="flex items-center gap-3"
                  >
                    <InitialsAvatar name={client.clientName} />
                    <span className="font-medium">{client.clientName}</span>
                  </TableCellLink>
                  <TableCell className="text-right font-semibold">{amount(client.cash)}</TableCell>
                  <TableCell className="pl-12">
                    {client.shareOfPortfolio !== null && (
                      <div className="flex items-center gap-3">
                        <div className="h-1.5 w-30 overflow-hidden rounded-sm bg-muted">
                          <div
                            className="h-full rounded-sm bg-success"
                            style={{
                              width: `${Math.min(1, client.shareOfPortfolio / SHARE_BAR_FULL) * 100}%`,
                            }}
                          />
                        </div>
                        <span className="text-muted-foreground">
                          {percent(client.shareOfPortfolio)}
                        </span>
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ))}
    </WidgetCard>
  );
}
